import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Heart, Zap, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-orange-500">Firebyte Websites</span>
            </h1>
            <p className="text-xl text-gray-400">
              We help auto shops compete with bigger chains using simple, affordable AI tools that actually work
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-6 px-6 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full">
                <span className="text-orange-400 font-semibold text-sm">Our Mission</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Built For Small Automotive Businesses
              </h2>

              <div className="space-y-4 text-lg text-gray-300">
                <p>
                  Big chain auto shops have call centers, booking systems, and marketing teams. Small independent shops? They're usually just the owner and a few technicians trying to do everything.
                </p>
                <p>
                  That's not fair. And that's why we exist.
                </p>
                <p>
                  <span className="text-orange-500 font-semibold">Firebyte Websites</span> was built to give small automotive businesses access to the same AI-powered tools that big companies use — without the big company price tag or complexity.
                </p>
                <p>
                  We believe every auto shop owner should be able to capture every lead, answer every call, and grow their business without working 80-hour weeks.
                </p>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/2244746/pexels-photo-2244746.jpeg"
                alt="Auto shop professional"
                className="rounded-2xl shadow-2xl shadow-orange-500/20"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              What We Stand For
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Heart,
                title: "No Tech Jargon",
                description: "We explain everything in plain English. No confusing terminology or buzzwords."
              },
              {
                icon: Target,
                title: "Results-Focused",
                description: "We only build things that help you capture more leads and save time. No fluff."
              },
              {
                icon: Zap,
                title: "Fast Implementation",
                description: "We know you're busy. We get you up and running quickly without sacrificing quality."
              },
              {
                icon: Users,
                title: "True Partnership",
                description: "We're not an agency that disappears after launch. We're your long-term partner."
              }
            ].map((value, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-all duration-300 hover:translate-y-[-4px]">
                <CardContent className="p-8 text-center">
                  <div className="bg-orange-500/10 w-16 h-16 rounded-xl flex items-center justify-center mb-6 mx-auto">
                    <value.icon className="h-8 w-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/3184430/pexels-photo-3184430.jpeg"
                alt="Partnership and collaboration"
                className="rounded-2xl shadow-2xl shadow-orange-500/20"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent rounded-2xl"></div>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Why We're <span className="text-orange-500">Different</span>
              </h2>

              <p className="text-lg text-gray-300 mb-8">
                Most web agencies build pretty websites and disappear. Most AI companies are focused on big enterprises with big budgets. We're different.
              </p>

              <div className="space-y-6">
                {[
                  {
                    title: "We Specialize in Automotive",
                    desc: "We only work with auto shops, so we understand your specific challenges and customer behaviors."
                  },
                  {
                    title: "We Focus on ROI",
                    desc: "Every feature we build is designed to either capture more leads or save you time. If it doesn't do that, we don't build it."
                  },
                  {
                    title: "We're With You Long-Term",
                    desc: "We maintain, update, and improve your systems ongoing. You're not just buying a website, you're getting a partner."
                  },
                  {
                    title: "We Keep It Simple",
                    desc: "No complicated dashboards or tech skills required. Everything just works in the background."
                  }
                ].map((point, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{point.title}</h3>
                      <p className="text-gray-400">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">
              Who We Help
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We work exclusively with US-based automotive businesses that want to grow without adding more staff
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: "Perfect Fit:",
                items: [
                  "Auto repair and maintenance shops",
                  "Mobile mechanics and detailers",
                  "Tire and brake specialty shops",
                  "Car washes and detailing centers",
                  "Shops missing 5+ calls per week",
                  "Businesses ready to grow systematically"
                ]
              },
              {
                title: "Not A Good Fit:",
                items: [
                  "Dealerships (we focus on service shops)",
                  "Shops outside the United States",
                  "Businesses not ready to invest in growth",
                  "Companies looking for DIY solutions",
                  "Shops wanting just a basic website",
                  "Businesses not willing to try AI tools"
                ]
              }
            ].map((section, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{section.title}</h3>
                  <div className="space-y-3">
                    {section.items.map((item, iIdx) => (
                      <div key={iIdx} className="flex items-start gap-3">
                        <CheckCircle className={`h-5 w-5 flex-shrink-0 mt-1 ${idx === 0 ? 'text-orange-500' : 'text-gray-500'}`} />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            If you're ready to stop losing customers to missed calls and outdated systems, let's talk
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-10 py-6 text-xl font-bold shadow-2xl transition-all duration-300 hover:scale-105">
              Book a Free Discovery Call
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
